// qs-usePanMode.ts
import { computed, onMounted, onUnmounted, readonly, ref } from 'vue'
import { TaperingEngine } from '@/brushes/engines/qs-brush-engine-tapering'
import { useModeManager } from '@/composables/qs-useModeManager'
import { useCanvasStore } from '@/stores/qs-store-canvas'
import type { KonvaEvent, KonvaStage } from '@/types/qs-type-konva'
import type { Point } from '@/types/qs-types-index'
import { cursorManager } from '@/utils/qs-cursorManager'

export function usePanMode() {
  const modeManager = useModeManager()
  const canvasStore = useCanvasStore()

  const isPanning = ref(false)
  const lastPanPosition = ref<Point | null>(null)
  const panStartTime = ref<number | null>(null)
  const panVelocity = ref<Point>({ x: 0, y: 0 })

  const panMode = computed(() => modeManager.isPanMode.value)
  const currentScale = computed(() => canvasStore.scale)
  const currentPosition = computed(() => canvasStore.position)

  const getCanvasPosition = (e: KonvaEvent | MouseEvent | TouchEvent, stage: KonvaStage): Point | null => {
    if ('touches' in e && e.touches && e.touches.length > 0) {
      const touch = e.touches[0]
      const rect = stage.container().getBoundingClientRect()
      return {
        x: (touch.clientX - rect.left) / currentScale.value,
        y: (touch.clientY - rect.top) / currentScale.value
      }
    }

    if ('clientX' in e && e.clientX !== undefined && e.clientY !== undefined) {
      const rect = stage.container().getBoundingClientRect()
      return {
        x: (e.clientX - rect.left) / currentScale.value,
        y: (e.clientY - rect.top) / currentScale.value
      }
    }

    return null
  }

  const setupPanEventListeners = () => {
  }

  const cleanupPanEventListeners = () => {
  }

  const startPanning = (e: KonvaEvent | MouseEvent | TouchEvent, stage: KonvaStage) => {
    if (!modeManager.isPanMode.value) return

    const pos = getCanvasPosition(e, stage)
    if (!pos) return

    isPanning.value = true
    lastPanPosition.value = pos
    panStartTime.value = Date.now()
    panVelocity.value = { x: 0, y: 0 }
    cursorManager.setCursor('panning')
  }

  const continuePanning = (e: KonvaEvent | MouseEvent | TouchEvent, stage: KonvaStage) => {
    if (!modeManager.isPanMode.value || !isPanning.value || !lastPanPosition.value) return

    const pos = getCanvasPosition(e, stage)
    if (!pos) return

    const deltaX = pos.x - lastPanPosition.value.x
    const deltaY = pos.y - lastPanPosition.value.y

    const newPosition = {
      x: currentPosition.value.x + deltaX,
      y: currentPosition.value.y + deltaY
    }

    canvasStore.setPosition(newPosition)

    const now = Date.now()
    if (panStartTime.value) {
      const timeDelta = now - panStartTime.value
      if (timeDelta > 0) {
        panVelocity.value = {
          x: deltaX / timeDelta,
          y: deltaY / timeDelta
        }
      }
    }

    lastPanPosition.value = pos
  }

  const endPanning = () => {
    if (!isPanning.value) return

    isPanning.value = false
    lastPanPosition.value = null
    panStartTime.value = null
    cursorManager.setCursor('pan')
  }

  const setZoom = (scale: number, center?: Point, options: {
    forceImmediate?: boolean
    enableQualityScaling?: boolean
    enableThrottling?: boolean
  } = {}) => {
    const newScale = Math.max(0.1, Math.min(10, scale))

    TaperingEngine.onZoomActivity()

    const zoomFactor = newScale / currentScale.value

    canvasStore.zoom(zoomFactor, center, {
      forceImmediate: options.forceImmediate || false,
      enableQualityScaling: options.enableQualityScaling !== false,
      enableThrottling: options.enableThrottling !== false
    })
  }

  const zoomIn = (center?: Point) => {
    const newScale = Math.min(currentScale.value * 1.2, 10)
    setZoom(newScale, center)
  }

  const zoomOut = (center?: Point) => {
    const newScale = Math.max(currentScale.value / 1.2, 0.1)
    setZoom(newScale, center)
  }

  const resetZoom = () => {
    setZoom(1)
    canvasStore.setPosition({ x: 0, y: 0 })
  }

  const fitToScreen = () => {
    const stage = canvasStore.stage
    if (!stage) return

    const container = stage.container()
    const containerWidth = container.clientWidth
    const containerHeight = container.clientHeight

    const scaleX = containerWidth / canvasStore.width
    const scaleY = containerHeight / canvasStore.height
    const scale = Math.min(scaleX, scaleY, 1)

    setZoom(scale)
    canvasStore.setPosition({ x: 0, y: 0 })
  }

  const activatePanMode = async () => {
    setupPanEventListeners()
    cursorManager.setCursor('pan')
    isPanning.value = false
    lastPanPosition.value = null
    panStartTime.value = null
    panVelocity.value = { x: 0, y: 0 }
  }

  const deactivatePanMode = async () => {
    if (isPanning.value) {
      endPanning()
    }

    cleanupPanEventListeners()
    cursorManager.resetToDrawing()
  }

  let unsubscribeActivate: (() => void) | null = null
  let unsubscribeDeactivate: (() => void) | null = null

  onMounted(() => {
    unsubscribeActivate = modeManager.onModeEvent((event) => {
      if (event.type === 'mode-activated' && event.mode === 'pan') {
        activatePanMode()
      }
    })

    unsubscribeDeactivate = modeManager.onModeEvent((event) => {
      if (event.type === 'mode-deactivated' && event.mode === 'pan') {
        deactivatePanMode()
      }
    })
  })

  onUnmounted(() => {
    if (isPanning.value) {
      endPanning()
    }

    cleanupPanEventListeners()

    if (unsubscribeActivate) {
      unsubscribeActivate()
    }

    if (unsubscribeDeactivate) {
      unsubscribeDeactivate()
    }
  })

  return {
    isPanning: readonly(isPanning),
    lastPanPosition: readonly(lastPanPosition),
    panStartTime: readonly(panStartTime),
    panVelocity: readonly(panVelocity),
    panMode,
    currentScale,
    currentPosition,
    startPanning,
    continuePanning,
    endPanning,
    zoomIn,
    zoomOut,
    setZoom,
    resetZoom,
    fitToScreen,
    activatePanMode,
    deactivatePanMode
  }
}